import { App } from '@capacitor/app';
import { Camera } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Clipboard } from '@capacitor/clipboard';
import { Geolocation } from '@capacitor/geolocation';
import { AppLauncher } from '@capacitor/app-launcher';
import { BleClient } from '@capacitor-community/bluetooth-le';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { withTimeout, TimeoutError } from 'src/utils/promise-timeout';
import LocalNetworkPermission from 'src/utils/plugins/local-network-permission';
import {
  openAndroidAppSettings,
  openAndroidLocationSettings,
} from 'src/utils/plugins/device-settings';

import { starPrintService } from 'src/services/printers';

import { REQUIRED_PERMISSIONS } from 'src/sections/system/permissions/constants';
import {
  PermissionId,
  PermissionState,
  DevicePermission,
  PermissionCheckResult,
} from 'src/sections/system/permissions/types';

const LOCATION_SERVICES_DISABLED_ERROR_CODE = 'OS-PLUG-GLOC-0007';

// A real clipboard write+read settles in well under a second; only the
// human-gated iOS "Allow Paste?" alert exceeds this.
const CLIPBOARD_PROBE_TIMEOUT_MS = 3_000;

// Normalize permission states from different plugins to our standard states
function normalizePermissionState(state: string | undefined): PermissionState {
  if (!state) return 'prompt';

  switch (state.toLowerCase()) {
    case 'granted':
      return 'granted';
    case 'denied':
    case 'restricted':
      return 'denied';
    case 'prompt':
    case 'prompt-with-rationale':
    case 'limited':
      return state as PermissionState;
    default:
      return 'prompt';
  }
}

function isLocationServicesDisabledError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;

  const pluginError = error as { code?: unknown; message?: unknown };
  const code = typeof pluginError.code === 'string' ? pluginError.code : undefined;
  const message = typeof pluginError.message === 'string' ? pluginError.message : undefined;

  return (
    code === LOCATION_SERVICES_DISABLED_ERROR_CODE ||
    message?.toLowerCase().includes('location services are not enabled') === true
  );
}

// Check camera permission
async function checkCameraPermission(): Promise<PermissionState> {
  try {
    const status = await Camera.checkPermissions();
    return normalizePermissionState(status.camera);
  } catch (error) {
    console.error('[Permissions] Error checking camera permission:', error);
    return 'prompt';
  }
}

// Request camera permission
async function requestCameraPermission(): Promise<PermissionState> {
  try {
    const status = await Camera.requestPermissions({ permissions: ['camera'] });
    return normalizePermissionState(status.camera);
  } catch (error) {
    console.error('[Permissions] Error requesting camera permission:', error);
    return 'denied';
  }
}

// Check location permission
async function checkLocationPermission(): Promise<PermissionState> {
  try {
    const status = await Geolocation.checkPermissions();
    return normalizePermissionState(status.location);
  } catch (error) {
    if (isLocationServicesDisabledError(error)) return 'settings-disabled';
    console.error('[Permissions] Error checking location permission:', error);
    return 'prompt';
  }
}

// Request location permission
async function requestLocationPermission(): Promise<PermissionState> {
  try {
    const status = await Geolocation.requestPermissions({ permissions: ['location'] });
    return normalizePermissionState(status.location);
  } catch (error) {
    if (isLocationServicesDisabledError(error)) return 'settings-disabled';
    console.error('[Permissions] Error requesting location permission:', error);
    return 'denied';
  }
}

// Check Bluetooth permission WITHOUT prompting.
//
// Reads CBManager.authorization (iOS) / the BLUETOOTH_CONNECT permission state
// (Android) via the StarPrint plugin. Neither instantiates a Bluetooth manager,
// so — unlike BleClient.initialize() — this never triggers the iOS permission
// prompt at boot. The actual prompt is deferred to in-context Bluetooth use
// (printer discovery / reconnect), which is the correct iOS moment.
export async function checkBluetoothPermission(): Promise<PermissionState> {
  try {
    const { status } = await starPrintService.getBluetoothAuthorization();
    switch (status) {
      case 'authorized':
        return 'granted';
      case 'denied':
      case 'restricted':
        return 'denied';
      case 'notDetermined':
        return 'prompt';
      default:
        return 'granted'; // 'unknown'/unsupported — never block startup
    }
  } catch (error) {
    console.warn('[Permissions] Silent Bluetooth check failed:', error);
    return 'prompt';
  }
}

// Request Bluetooth permission
async function requestBluetoothPermission(): Promise<PermissionState> {
  try {
    // Initialize BLE client - this triggers permission request on Android
    await BleClient.initialize({ androidNeverForLocation: false });

    // Try to check if Bluetooth is enabled (this also validates permissions)
    await BleClient.isEnabled();

    return 'granted';
  } catch (error: unknown) {
    console.error('[Permissions] Error requesting Bluetooth permission:', error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (
      errorMessage.toLowerCase().includes('permission') ||
      errorMessage.toLowerCase().includes('denied')
    ) {
      return 'denied';
    }
    return 'denied';
  }
}

/**
 * Whether Bluetooth is actually powered ON (distinct from permission —
 * `checkBluetoothPermission` returns 'granted' even when the radio is off).
 * `BleClient.initialize()` must run before `isEnabled()`; a throw means we can't
 * confirm it's on, so we treat that as "off/unknown" and let the caller prompt.
 */
export async function isBluetoothEnabled(): Promise<boolean> {
  try {
    await BleClient.initialize({ androidNeverForLocation: false });
    return await BleClient.isEnabled();
  } catch (error) {
    console.warn('[Permissions] Bluetooth enabled-check failed:', error);
    return false;
  }
}

/**
 * Ask the OS to turn Bluetooth on. Android shows a system enable prompt; iOS
 * has no programmatic enable (the user must toggle it in Control Center), so
 * this is a no-op there and the UI shows guidance copy instead.
 */
export async function promptEnableBluetooth(): Promise<void> {
  if (Capacitor.getPlatform() !== 'android') return;
  try {
    await BleClient.initialize({ androidNeverForLocation: false });
    await BleClient.requestEnable();
  } catch (error) {
    console.warn('[Permissions] requestEnable failed:', error);
  }
}

// Check local network permission (iOS only)
async function checkLocalNetworkPermission(): Promise<PermissionState> {
  try {
    const platform = Capacitor.getPlatform();

    // Only iOS requires local network permission
    if (platform !== 'ios') {
      return 'granted';
    }

    const result = await LocalNetworkPermission.checkPermission();
    return normalizePermissionState(result.state);
  } catch (error) {
    console.error('[Permissions] Error checking local network permission:', error);
    return 'prompt';
  }
}

// Request local network permission (iOS only)
async function requestLocalNetworkPermission(): Promise<PermissionState> {
  try {
    const platform = Capacitor.getPlatform();

    // Only iOS requires local network permission
    if (platform !== 'ios') {
      return 'granted';
    }

    const result = await LocalNetworkPermission.requestPermission();
    return normalizePermissionState(result.state);
  } catch (error) {
    console.error('[Permissions] Error requesting local network permission:', error);
    return 'denied';
  }
}

// Check clipboard permission (iOS only)
async function checkClipboardPermission(): Promise<PermissionState> {
  try {
    const platform = Capacitor.getPlatform();

    // Only iOS 16+ requires clipboard permission
    // On Android and web, clipboard access doesn't require runtime permission
    if (platform !== 'ios') {
      return 'granted';
    }

    // iOS doesn't have a dedicated checkPermission API for clipboard
    // We verify access by attempting to write and read from clipboard
    const probeClipboard = async (): Promise<PermissionState> => {
      const testString = `PointCraft_check_${Date.now()}`;
      await Clipboard.write({ string: testString });
      const result = await Clipboard.read();

      // If we can write and read, clipboard access is granted
      if (result.value) {
        return 'granted';
      }

      return 'prompt';
    };

    // Clipboard.read() can sit behind the iOS "Allow Paste?" alert until a human
    // answers it - it must never hang callers like the boot permission check.
    return await withTimeout(
      probeClipboard(),
      CLIPBOARD_PROBE_TIMEOUT_MS,
      'iOS clipboard probe timed out'
    );
  } catch (error) {
    if (error instanceof TimeoutError) {
      // Alert pending; no POS feature reads the clipboard, so don't block on it.
      return 'granted';
    }
    console.error('[Permissions] Error checking clipboard permission:', error);
    return 'prompt';
  }
}

// Request clipboard permission (iOS only)
async function requestClipboardPermission(): Promise<PermissionState> {
  try {
    const platform = Capacitor.getPlatform();

    // Only iOS 16+ requires clipboard permission
    if (platform !== 'ios') {
      return 'granted';
    }

    console.log('[Permissions] Requesting clipboard permission...');

    // Step 1: Write test data to clipboard
    const testString = `PointCraft_clipboard_test_${Date.now()}`;
    await Clipboard.write({
      string: testString,
    });
    console.log('[Permissions] Wrote test data to clipboard');

    // Step 2: Try to read it back - this verifies clipboard access is working
    const result = await Clipboard.read();
    console.log('[Permissions] Read from clipboard:', result.type, result.value?.substring(0, 50));

    // Step 3: Verify we got our test data back (or any data)
    if (result.value) {
      console.log('[Permissions] Clipboard read successful - permission granted');
      return 'granted';
    }

    // If we got here but no value, still consider it granted since read didn't fail
    console.log('[Permissions] Clipboard read completed but empty - assuming granted');
    return 'granted';
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('[Permissions] Clipboard operation failed:', errorMessage);

    // Check if error indicates permission denied
    if (
      errorMessage.toLowerCase().includes('permission') ||
      errorMessage.toLowerCase().includes('denied') ||
      errorMessage.toLowerCase().includes('not allowed') ||
      errorMessage.toLowerCase().includes('restricted')
    ) {
      console.log('[Permissions] Clipboard permission denied');
      return 'denied';
    }

    // For other errors, try one more time with just a read
    // This handles the case where write succeeded but read failed for other reasons
    try {
      await Clipboard.read();
      return 'granted';
    } catch {
      // If read also fails, assume denied
      console.log('[Permissions] Clipboard fallback read also failed');
      return 'denied';
    }
  }
}

// Open app settings
export async function openAppSettings(): Promise<void> {
  const platform = Capacitor.getPlatform();

  try {
    if (platform === 'ios') {
      // On iOS, open the app-specific settings page
      await AppLauncher.openUrl({ url: 'app-settings:' });
    } else if (platform === 'android') {
      await openAndroidAppSettings();
    }
  } catch (error) {
    console.error('[Permissions] Error opening app settings:', error);

    if (platform === 'ios') {
      try {
        await AppLauncher.openUrl({ url: 'app-settings:' });
      } catch {
        console.error('[Permissions] Fallback settings open also failed');
      }
    }
  }
}

// Open the device-wide Location Services page when Android reports that the
// location provider itself is disabled rather than an app permission denial.
export async function openLocationSettings(): Promise<void> {
  if (Capacitor.getPlatform() !== 'android') {
    await openAppSettings();
    return;
  }

  try {
    await openAndroidLocationSettings();
  } catch (error) {
    console.error('[Permissions] Error opening location settings:', error);
  }
}

// Check all permissions
export async function checkAllPermissions(): Promise<PermissionCheckResult> {
  const platform = Capacitor.getPlatform();

  // Skip on web
  if (!Capacitor.isNativePlatform()) {
    return {
      allGranted: true,
      permissions: {
        bluetooth: 'granted',
        location: 'granted',
        camera: 'granted',
        localNetwork: 'granted',
        clipboard: 'granted',
      },
      deniedPermissions: [],
    };
  }

  const [bluetooth, location, camera, localNetwork, clipboard] = await Promise.all([
    checkBluetoothPermission(),
    checkLocationPermission(),
    checkCameraPermission(),
    checkLocalNetworkPermission(),
    checkClipboardPermission(),
  ]);

  const deniedPermissions: PermissionId[] = [];
  // Bluetooth intentionally does NOT gate startup: it's only needed for Bluetooth
  // printers/readers (optional, handled in-context), and forcing it here would
  // mean prompting at boot. Its state is still reported below for display, and
  // Bluetooth problems surface via the footer printer-help flow.
  if (location !== 'granted') deniedPermissions.push('location');
  if (camera !== 'granted') deniedPermissions.push('camera');

  // Only check iOS-specific permissions on iOS
  if (platform === 'ios') {
    if (localNetwork !== 'granted') deniedPermissions.push('localNetwork');
    // Clipboard, like Bluetooth above, intentionally does NOT gate startup: no
    // POS feature reads the clipboard, and its iOS probe can sit behind the
    // human-gated "Allow Paste?" alert. Its state is still reported below.
  }

  return {
    allGranted: deniedPermissions.length === 0,
    permissions: {
      bluetooth,
      location,
      camera,
      localNetwork,
      clipboard,
    },
    deniedPermissions,
  };
}

// Request a single permission
export async function requestPermission(id: PermissionId): Promise<PermissionState> {
  switch (id) {
    case 'bluetooth':
      return requestBluetoothPermission();
    case 'location':
      return requestLocationPermission();
    case 'camera':
      return requestCameraPermission();
    case 'localNetwork':
      return requestLocalNetworkPermission();
    case 'clipboard':
      return requestClipboardPermission();
    default:
      return 'denied';
  }
}

// Request all permissions sequentially
export async function requestAllPermissions(): Promise<PermissionCheckResult> {
  const platform = Capacitor.getPlatform();

  // Request in order: Bluetooth first (most important for POS), then Location, then Camera
  await requestBluetoothPermission();
  await requestLocationPermission();
  await requestCameraPermission();

  // Request iOS-specific permissions on iOS
  if (platform === 'ios') {
    await requestLocalNetworkPermission();
    await requestClipboardPermission();
  }

  // Check final status
  return checkAllPermissions();
}

// Sort permissions with ungranted ones at the top
function sortPermissionsByStatus(permissions: DevicePermission[]): DevicePermission[] {
  return [...permissions].sort((a, b) => {
    // Granted permissions go to the bottom
    const aGranted = a.status === 'granted' ? 1 : 0;
    const bGranted = b.status === 'granted' ? 1 : 0;
    return aGranted - bGranted;
  });
}

// Hook for managing device permissions
export function useDevicePermissions() {
  const platform = Capacitor.getPlatform();

  // Filter permissions based on current platform
  const platformPermissions = useMemo(
    () =>
      REQUIRED_PERMISSIONS.filter((p) => {
        // If no platforms specified, show on all platforms
        if (!p.platforms || p.platforms.length === 0) return true;
        // Check if current platform is in the list
        return p.platforms.includes(platform as 'ios' | 'android' | 'web');
      }),
    [platform]
  );

  const [permissions, setPermissions] = useState<DevicePermission[]>(
    platformPermissions.map((p) => ({ ...p }))
  );
  const [isChecking, setIsChecking] = useState(false);
  const [allGranted, setAllGranted] = useState(false);

  // Update permissions when platform permissions change
  useEffect(() => {
    setPermissions(platformPermissions.map((p) => ({ ...p })));
  }, [platformPermissions]);

  // Check all permissions
  const checkPermissions = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      setAllGranted(true);
      setPermissions((prev) => prev.map((p) => ({ ...p, status: 'granted' as PermissionState })));
      return;
    }

    setIsChecking(true);

    try {
      const result = await checkAllPermissions();

      setPermissions((prev) =>
        sortPermissionsByStatus(
          prev.map((p) => ({
            ...p,
            status: result.permissions[p.id],
          }))
        )
      );

      setAllGranted(result.allGranted);
    } catch (error) {
      console.error('[Permissions] Error checking permissions:', error);
    } finally {
      setIsChecking(false);
    }
  }, []);

  // Request a single permission
  const requestSinglePermission = useCallback(
    async (id: PermissionId) => {
      setIsChecking(true);

      try {
        const newStatus = await requestPermission(id);

        setPermissions((prev) =>
          sortPermissionsByStatus(prev.map((p) => (p.id === id ? { ...p, status: newStatus } : p)))
        );

        // Re-check all permissions to update allGranted state
        await checkPermissions();
      } catch (error) {
        console.error(`[Permissions] Error requesting ${id} permission:`, error);
      } finally {
        setIsChecking(false);
      }
    },
    [checkPermissions]
  );

  // Request all permissions
  const requestAll = useCallback(async () => {
    setIsChecking(true);

    try {
      const result = await requestAllPermissions();

      setPermissions((prev) =>
        sortPermissionsByStatus(
          prev.map((p) => ({
            ...p,
            status: result.permissions[p.id],
          }))
        )
      );

      setAllGranted(result.allGranted);
    } catch (error) {
      console.error('[Permissions] Error requesting all permissions:', error);
    } finally {
      setIsChecking(false);
    }
  }, []);

  // Check if a specific permission is denied (needs settings)
  const isPermissionDenied = useCallback(
    (id: PermissionId): boolean => {
      const permission = permissions.find((p) => p.id === id);
      return permission?.status === 'denied';
    },
    [permissions]
  );

  // Check permissions on mount
  useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);

  // Listen for app state changes to re-check permissions when returning from settings
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        // Small delay to ensure settings changes are reflected
        setTimeout(() => {
          checkPermissions();
        }, 500);
      }
    });

    return () => {
      listener.then((l) => l.remove());
    };
  }, [checkPermissions]);

  return {
    permissions,
    isChecking,
    allGranted,
    checkPermissions,
    requestPermission: requestSinglePermission,
    requestAllPermissions: requestAll,
    isPermissionDenied,
    openAppSettings,
    openLocationSettings,
  };
}
